import {
  Position,
  RndResizeStartCallback,
  type Props,
  type RndResizeCallback,
} from "react-rnd";
import { type DraggableEventHandler } from "react-draggable";
import { useCallback, useMemo, useLayoutEffect, useEffect } from "react";
import rndDefaults, {
  RESIZING_DISABLED,
  RESIZING_ENABLED,
} from "./rndDefaults";
import useDraggable from "./useDraggable";
import useResizable, { Size } from "./useResizable";
import { isWindowOutsideBounds } from "../functions";
import { useWindows } from "@/contexts/windows";
import { useSession } from "@/contexts/session";
import { getElementSize, pxToNum } from "@/utils/functions";
import { useSignal } from "@/contexts/signal";
import { useMainElements } from "@/contexts/elements";
import { useWindow } from "@/contexts/windows/window";
import { parseSnap } from "../functions";
import useWindowActions from "../Titlebar/useWindowActions";
import { SnapInfo, SnapType } from "@/contexts/windows/types";
import { TRANSITIONS_IN_MILLISECONDS } from "@/utils/constants";

const enableIframeCapture = (enable = true): void =>
  document.querySelectorAll("iframe").forEach((iframe) => {
    // eslint-disable-next-line no-param-reassign
    iframe.style.pointerEvents = enable ? "initial" : "none";
  });

const useRnd = (id: string): Props => {
  const {
    windows: {
      [id]: {
        snap = false,
        allowResizing = true,
        autoSizing = false,
        lockAspectRatio = false,
        maximized = false,
      } = {},
      ...otherWindows
    },
    setSnap,
  } = useWindows();
  const { events: RD_Events } = useSignal();
  const { Events: RD_WindowEvents } = useWindow();
  const { setWindowStates } = useSession();
  const { onMaximize } = useWindowActions(id);
  const { WindowsViewArea, getLatestSize } = useMainElements();
  const [size, setSize] = useResizable(id, autoSizing);
  const [position, setPosition] = useDraggable(id, size);
  const isSnap = snap && typeof snap === "object";

  useLayoutEffect(() => {
    // Reminder: snapBasePos, are the zero values to work everything out with dimensions.
    // so if were to position on the right side of screen would be:
    // x = snapBasePos.x + parentSize.width / 2; for example...
    const updateSnapDetails = (windowsViewSize: Size) => {
      if (isSnap) {
        const relatedWindows = Object.entries(otherWindows).filter(
          ([id, { snap: someWindowSnap, closing }]) => {
            return (
              !closing &&
              someWindowSnap &&
              typeof someWindowSnap === "object" &&
              someWindowSnap.type === snap.type &&
              someWindowSnap.index !== snap.index
            );
          }
        );
        const parsedSnap = parseSnap(
          snap as SnapInfo,
          windowsViewSize,
          relatedWindows
        );
        if (position.x !== parsedSnap.x || position.y !== parsedSnap.y) {
          setPosition({
            x: parsedSnap.x,
            y: parsedSnap.y,
          });
        }
        if (
          size.width !== parsedSnap.width ||
          size.height !== parsedSnap.height
        ) {
          setSize({
            width: parsedSnap.width || 0,
            height: parsedSnap.height || 0,
          });
        }
      }
    };

    const registeredEvents = [
      RD_Events.on("event:elements/resize/windowsview", updateSnapDetails),
    ];

    return () => {
      // Cleanup
      registeredEvents.forEach(RD_Events.removeListenerByID);
    };
  }, [
    snap,
    otherWindows,
    position,
    WindowsViewArea,
    RD_Events,
    isSnap,
    size,
    setPosition,
    setSize,
  ]);
  const updateWindowState = useCallback(
    (newPosition, newSize) => {
      setWindowStates((currentWindowStates) => ({
        ...currentWindowStates,
        [id]: {
          ...currentWindowStates[id],
          position: newPosition,
          size: newSize,
        },
      }));
    },
    [id, setWindowStates]
  );
  useEffect(() => {
    if (isSnap) {
      const parentSize = WindowsViewArea
        ? getElementSize(WindowsViewArea)
        : getLatestSize("WindowsViewArea");

      const relatedWindows = Object.entries(otherWindows).filter(
        ([id, { snap: someWindowSnap, closing }]) => {
          return (
            !closing &&
            someWindowSnap &&
            typeof someWindowSnap === "object" &&
            someWindowSnap.type === snap.type &&
            someWindowSnap.index !== snap.index
          );
        }
      );
      const parsedSnap = parseSnap(
        snap as SnapInfo,
        parentSize,
        relatedWindows
      );
      // console.log({ parsedSnap });
      if (position.x !== parsedSnap.x || position.y !== parsedSnap.y) {
        setPosition({
          x: parsedSnap.x,
          y: parsedSnap.y,
        });
      }
      if (
        size.width !== parsedSnap.width ||
        size.height !== parsedSnap.height
      ) {
        setSize({
          width: parsedSnap.width || 0,
          height: parsedSnap.height || 0,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [WindowsViewArea, getLatestSize, snap]);

  const disableIframeCapture = useCallback(
    () => enableIframeCapture(false),
    []
  );

  const onDragStart: DraggableEventHandler = useCallback(
    (event, { x, y }) => {
      // * IMPORTANT: Disable any snap status when dragging. and apply previous data.
      disableIframeCapture();
      // console.log("onDragStart");
      setSnap(id, false)
      if (maximized) {
        setPosition((prevPos) => ({
          ...prevPos,
          y: 0,
        }));
        onMaximize();
      }
      const startPosition: Position = { x, y };
      // events:
      const eventMsg = { position: startPosition, id };
      RD_WindowEvents.emit("event:window/drag_start", eventMsg);
      RD_Events.emit(
        ["event:windows/drag_start", `event:windows/drag_start:${id}`],
        eventMsg
      );
    },
    [disableIframeCapture, setSnap, id, maximized, RD_WindowEvents, RD_Events, setPosition, onMaximize]
  );

  const onDragStop: DraggableEventHandler = useCallback(
    (_event, { x, y }) => {
      enableIframeCapture();
      const newPosition = { x, y };
      const windowsContainerSize = WindowsViewArea
        ? getElementSize(WindowsViewArea)
        : getLatestSize("WindowsViewArea");
      if (
        !isWindowOutsideBounds(
          { position: newPosition, size },
          {
            x: pxToNum(windowsContainerSize.width),
            y: pxToNum(windowsContainerSize.height),
          },
          true
        )
      ) {
        const isSnap = snap && typeof snap === "object";
        setPosition((currentPos) => {
          const differences = {
            x: Math.abs(newPosition.x - currentPos.x),
            y: Math.abs(newPosition.y - currentPos.y),
          };
          if (differences.x >= 5 || differences.y >= 5) {
            // Actual position change:
            if (isSnap) {
              const windowsContainerSize = WindowsViewArea
                ? getElementSize(WindowsViewArea)
                : getLatestSize("WindowsViewArea");
              const data = parseSnap(snap, windowsContainerSize);

              setSize({
                width: data.width || 0,
                height: data.height || 0,
              });
              setPosition({
                x: data.x || 0,
                y: data.y || 0,
              });

              const relatedWindows = Object.entries(otherWindows).filter(
                ([id, { snap: someWindowSnap, closing }]) => {
                  return (
                    !closing &&
                    someWindowSnap &&
                    typeof someWindowSnap === "object" &&
                    someWindowSnap.type === snap.type &&
                    someWindowSnap.index !== snap.index
                  );
                }
              );

              relatedWindows.forEach(
                ([someWindowID, someWindowSnap], thatWindowIndex) => {
                  let newSnap: SnapType;
                  if (
                    typeof someWindowSnap === "object" &&
                    someWindowSnap !== null
                  ) {
                    const snapInfo = { ...someWindowSnap } as SnapInfo;

                    if (snapInfo.type === "threeColumns") {
                      snapInfo.type = "twoColumns";
                      snapInfo.widthPercent = 50;
                      snapInfo.index = thatWindowIndex;
                    } else if (snapInfo.type === "twoColumns") {
                      newSnap = false;
                    } else {
                      newSnap = snapInfo;
                    }

                    newSnap = snapInfo;
                  } else {
                    newSnap = false;
                  }

                  setSnap(someWindowID, newSnap);
                }
              );

              setSnap(id, false);
            }
          }
          return newPosition;
        });
        updateWindowState(newPosition, size);
        // events
        const eventMsg = { position: newPosition, id };
        RD_WindowEvents.emit("event:window/drag_stop", eventMsg);
        RD_Events.emit(
          ["event:windows/drag_stop", `event:windows/drag_stop:${id}`],
          eventMsg
        );
      }
    },
    [
      WindowsViewArea,
      getLatestSize,
      size,
      snap,
      setPosition,
      updateWindowState,
      RD_Events,
      RD_WindowEvents,
      id,
      setSize,
      otherWindows,
      setSnap,
    ]
  );

  const onResizeStart: RndResizeStartCallback = useCallback(
    (event, direction, ref) => {
      disableIframeCapture();
      const currentSize = {
        width: pxToNum(ref.style.width),
        height: pxToNum(ref.style.height),
      };
      const eventMsg = { size: currentSize, id };
      RD_WindowEvents.emit("event:window/resize_start", eventMsg);
      RD_Events.emit(
        ["event:windows/resize_start", `event:windows/resize_start:${id}`],
        eventMsg
      );
    },
    [RD_Events, RD_WindowEvents, id, disableIframeCapture]
  );

  const onResizeStop: RndResizeCallback = useCallback(
    (
      _event,
      _direction,
      { style: { height, width, transform } },
      _delta,
      resizePosition
    ) => {
      const [, x, y] =
        transform.match(/translate\((-?\d+)px, (-?\d+)px\)/) || [];
      const newPosition =
        typeof x === "string" && typeof y === "string"
          ? { x: pxToNum(x), y: pxToNum(y) }
          : resizePosition;
      enableIframeCapture();
      const newSize = { height: pxToNum(height), width: pxToNum(width) };
      if (newPosition.y < 0) {
        newSize.height += newPosition.y;
        newPosition.y = 0;
      }
      setPosition(newPosition);
      setSize(newSize);
      updateWindowState(newPosition, newSize);
      // Events:
      const eventMsg = {
        position: newPosition,
        size: newSize,
        id,
      };
      RD_WindowEvents.emit("event:window/resize_stop", eventMsg);
      RD_Events.emit(
        ["event:windows/resize_stop", `event:windows/resize_stop:${id}`],
        eventMsg
      );
    },
    [id, setPosition, setSize, RD_Events, RD_WindowEvents, updateWindowState]
  );

  const enableResizing = useMemo(
    () => (allowResizing && !maximized ? RESIZING_ENABLED : RESIZING_DISABLED),
    [allowResizing, maximized]
  );

  const onDrag = useCallback(
    (_event, { x, y }) => {
      const newPosition: Position = { x, y };
      // Events
      const eventMsg = {
        position: newPosition,
        id,
      };
      RD_WindowEvents.emit("event:window/drag", eventMsg);
      RD_Events.emit(
        ["event:windows/drag", `event:windows/drag:${id}`],
        eventMsg
      );
    },
    [RD_Events, RD_WindowEvents, id]
  );

  const onResize = useCallback(
    (
      _event,
      _direction,
      { style: { height, width } },
      _delta,
      resizePosition
    ) => {
      const newPosition: Position = resizePosition;
      const newSize = { height: pxToNum(height), width: pxToNum(width) };

      if (isSnap) {
        const parentSize = WindowsViewArea
          ? getElementSize(WindowsViewArea)
          : getLatestSize("WindowsViewArea");

        const relatedWindows = Object.entries(otherWindows).filter(
          ([id, { snap: someWindowSnap, closing }]) => {
            return (
              !closing &&
              someWindowSnap &&
              typeof someWindowSnap === "object" &&
              someWindowSnap.type === snap.type &&
              someWindowSnap.index !== snap.index
            );
          }
        );
        setTimeout(() => {
          const parsedSnap = parseSnap(
            snap as SnapInfo,
            parentSize,
            relatedWindows
          );
          // console.log({ snap, parsedSnap, onResize: true });
          if (position.x !== parsedSnap.x || position.y !== parsedSnap.y) {
            console.log("POSITION CHANGE");
            setPosition({
              x: parsedSnap.x || 0,
              y: parsedSnap.y || 0,
            });
          }
          if (
            newSize.width !== parsedSnap.width ||
            newSize.height !== parsedSnap.height
          ) {
            setSize({
              width: parsedSnap.width || 0,
              height: parsedSnap.height || 0,
            });
          }
        }, TRANSITIONS_IN_MILLISECONDS.WINDOW + 50);
      }
      // Events
      const eventMsg = {
        position: newPosition,
        size: newSize,
        id,
        user_resize: true,
      };
      RD_WindowEvents.emit("event:window/resize", eventMsg);
      RD_Events.emit(
        ["event:windows/resize", `event:windows/resize:${id}`],
        eventMsg
      );
    },
    [
      RD_WindowEvents,
      RD_Events,
      WindowsViewArea,
      getLatestSize,
      id,
      isSnap,
      otherWindows,
      position,
      setPosition,
      setSize,
      snap,
    ]
  );

  return {
    disableDragging: maximized,
    enableResizing,
    lockAspectRatio,
    onDrag,
    onDragStart,
    onDragStop,
    onResize,
    onResizeStart,
    onResizeStop,
    position,
    size,
    ...rndDefaults,
  };
};

export default useRnd;
